import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Camera } from "lucide-react";
import { api, errMsg, uploadPhoto } from "./api";
import { Shell, Panel, BigBtn, GhostBtn, Field, inputCls, Spinner } from "./ui";

const CATEGORIES = ["chemicals", "bathroom", "laundry", "coffee", "cleaning", "other"];
const STATUS_LABELS = { requested: "Requested", acknowledged: "Acknowledged", ordered: "Ordered", received: "Received", closed: "Closed", cancelled: "Cancelled" };

export default function Sundries() {
  const [suggestions, setSuggestions] = useState([]);
  const [requests, setRequests] = useState(null);
  const [item, setItem] = useState("");
  const [category, setCategory] = useState("other");
  const [location, setLocation] = useState("");
  const [quantity, setQuantity] = useState("");
  const [urgency, setUrgency] = useState("normal");
  const [note, setNote] = useState("");
  const [photoId, setPhotoId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/restock").then((r) => setRequests(r.data.requests)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
    api.get("/restock/suggestions").then((r) => setSuggestions(r.data.items)).catch(() => {});
  }, []);

  const openSameItem = requests?.find((r) => r.item.toLowerCase() === item.trim().toLowerCase());

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/restock", {
        item, category, location, quantity, urgency, note,
        photos: photoId ? [photoId] : [],
        dedupe_key: crypto.randomUUID(),
      });
      toast.success(`Request ${data.request.request_id} sent — the Pedal Pop team has been emailed`);
      setItem(""); setLocation(""); setQuantity(""); setNote(""); setUrgency("normal"); setPhotoId(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const stillLow = async (r) => {
    try {
      await api.post(`/restock/${r.request_id}/update`, { note: "Still low — flagged again by staff" });
      toast.success("Noted on the existing request");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Shell title="Sundries request" back="/staff">
      <Panel className="p-5 space-y-4">
        <Field label="Item">
          <input data-testid="sundries-item-input" value={item} onChange={(e) => setItem(e.target.value)} placeholder="e.g. Toilet paper" className={inputCls} />
        </Field>
        <div className="flex flex-wrap gap-2">
          {suggestions.map((s) => (
            <button key={s} data-testid={`sundries-suggestion-${s.replace(/\W+/g, "-").toLowerCase()}`} onClick={() => setItem(s)}
              className="min-h-[40px] px-3 rounded-full border border-white/15 text-xs text-white/60 active:bg-white/10">{s}</button>
          ))}
        </div>
        {openSameItem && item.trim() && (
          <div data-testid="sundries-duplicate-hint" className="rounded-xl border border-amber-400/40 bg-amber-400/5 p-3 text-sm">
            <p className="text-amber-400">Already requested: {openSameItem.request_id} ({STATUS_LABELS[openSameItem.status]})</p>
            <GhostBtn data-testid="sundries-still-low" onClick={() => stillLow(openSameItem)} className="mt-2 text-xs">Still low — add update</GhostBtn>
            <p className="text-white/40 text-xs mt-1.5">Or continue below to send a genuinely new request.</p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category">
            <select data-testid="sundries-category" value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Quantity (if known)">
            <input data-testid="sundries-quantity" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="e.g. 2 boxes" className={inputCls} />
          </Field>
        </div>
        <Field label="Location (optional)">
          <input data-testid="sundries-location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Rear bathroom" className={inputCls} />
        </Field>
        <Field label="Urgency">
          <div className="grid grid-cols-2 gap-2">
            {["normal", "urgent"].map((u) => (
              <button key={u} data-testid={`sundries-urgency-${u}`} onClick={() => setUrgency(u)}
                className={`min-h-[48px] rounded-xl border font-semibold uppercase text-sm ${urgency === u ? (u === "urgent" ? "border-[#ff2e4d] bg-[#ff2e4d]/15 text-[#ff2e4d]" : "border-white bg-white/10") : "border-white/15 text-white/50"}`}>
                {u}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Note (optional)">
          <textarea data-testid="sundries-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} className={inputCls + " py-3 min-h-[56px]"} />
        </Field>
        <label className="inline-flex items-center gap-2 text-sm text-white/60 min-h-[44px] cursor-pointer">
          <Camera size={18} /> <span>{photoId ? "Photo attached ✓" : "Add photo (optional)"}</span>
          <input data-testid="sundries-photo-input" type="file" accept="image/*" className="hidden"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              try { setPhotoId(await uploadPhoto(f, "restock")); toast.success("Photo uploaded"); }
              catch (ex) { toast.error(errMsg(ex)); }
            }} />
        </label>
        <BigBtn data-testid="sundries-send-button" onClick={submit} disabled={busy || !item.trim()}>
          {busy ? "Sending…" : "Send request"}
        </BigBtn>
      </Panel>

      <h3 className="text-xs uppercase tracking-[0.2em] text-white/45 mt-8 mb-3">Open requests</h3>
      {!requests ? <Spinner /> : requests.length === 0 ? (
        <p className="text-white/40 text-sm" data-testid="sundries-empty">Nothing open right now.</p>
      ) : (
        <div className="space-y-2.5" data-testid="sundries-request-list">
          {requests.map((r) => (
            <Panel key={r.request_id} className="p-4" data-testid={`sundries-request-${r.request_id}`}>
              <div className="flex justify-between items-start gap-3">
                <div>
                  <p className="font-semibold">{r.item} {r.urgency === "urgent" && <span className="text-[#ff2e4d] text-xs uppercase ml-1">Urgent</span>}</p>
                  <p className="text-xs text-white/45">{r.request_id} · {r.created_by} · {r.location || r.category}</p>
                </div>
                <span className="text-xs border border-white/20 rounded-full px-2.5 py-1 text-white/60 whitespace-nowrap">{STATUS_LABELS[r.status]}</span>
              </div>
              {r.timeline?.length > 1 && (
                <p className="text-xs text-white/35 mt-2">{r.timeline[r.timeline.length - 1].action} — {r.timeline[r.timeline.length - 1].by}</p>
              )}
            </Panel>
          ))}
        </div>
      )}
    </Shell>
  );
}
